/**
 * A case — Spec 025, D7. The header says which case it is, that it is a
 * «Пример решения» and where it stands, and the decision is right there, by
 * status (D2): a draft goes to review; a case on review is accepted or returned
 * by either person — its author may pull it back without a comment; an
 * accepted one is used in places; an archived one comes back as a draft.
 * Below, one block at a time: the text, where it went out, the discussion and
 * the files.
 */

import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Plus } from "lucide-react";

import { ApiError } from "@/api/client";
import { Cases, Me } from "@/api/resources";
import type { CaseDetailOut, CasePlaceKind, CasePlaceOut, CaseUseOut, UserOut } from "@/api/types";
import { AttachmentsSection } from "@/components/AttachmentsSection";
import {
  CASE_TEXTS,
  CaseFields,
  CaseState,
  demoLink,
  demoUrlError,
  draftOf,
  EXAMPLE_LABEL,
  patchOf,
  PLACE_KIND,
  webUrl,
  type CaseDraft,
} from "@/components/CaseForm";
import { ChatThread } from "@/components/ChatThread";
import { ConflictSheet } from "@/components/ConflictSheet";
import { Button } from "@/components/ui/Button";
import { SelectField, TextArea, TextField } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { Mark } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { AppBar, ago, blockPanel, BlockTabs, Caption, EmptyNote, whoDid, type BlockTab } from "@/kit";
import { formatDayMonth, pluralRu } from "@/lib/format";
import { Markdown } from "@/lib/markdown";
import { nameOf, usePeople } from "@/lib/people";
import { useBackButton } from "@/lib/telegram-ui";
import { useVersionedUpdate } from "@/lib/useVersionedUpdate";
import { spend } from "@/screens/StatsScreen";

type Block = "case" | "uses" | "discussion" | "files";
const BLOCKS: Block[] = ["case", "uses", "discussion", "files"];

type Step = "submit" | "accept" | "return" | "archive" | "restore";
const DONE: Record<Step, string> = {
  submit: "Кейс отправлен на проверку",
  accept: "Кейс принят — его можно использовать",
  return: "Кейс снова черновик",
  archive: "Кейс в архиве",
  restore: "Кейс вернулся из архива черновиком",
};

type CasePatch = Omit<Parameters<typeof Cases.update>[1], "version">;

export function CaseScreenRoute() {
  useBackButton("/knowledge/cases");
  const { case_id } = useParams<{ case_id: string }>();
  return case_id ? <CaseScreen caseId={case_id} /> : null;
}

/** `embedded`: in the right pane of «Кейсы» on a computer — no way back of its own. */
export function CaseScreen({ caseId, embedded = false }: { caseId: string; embedded?: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const people = usePeople();
  const me = useQuery({ queryKey: ["me"], queryFn: Me.get });
  const one = useQuery({ queryKey: ["cases", "one", caseId], queryFn: () => Cases.get(caseId) });
  const [editing, setEditing] = useState(false);
  const [using, setUsing] = useState(false);
  const [returning, setReturning] = useState(false);
  const [comment, setComment] = useState("");
  const [archiving, setArchiving] = useState(false);

  const refresh = () => {
    for (const key of [["cases"], ["overview"], ["chat"]]) void qc.invalidateQueries({ queryKey: key });
  };
  const act = useMutation({
    mutationFn: (step: Step) => {
      const { version } = one.data!;
      switch (step) {
        case "submit": return Cases.submit(caseId, version);
        case "accept": return Cases.accept(caseId, version);
        case "return": return Cases.returnToDraft(caseId, version, comment.trim() || undefined);
        case "archive": return Cases.archive(caseId, version);
        case "restore": return Cases.restore(caseId, version);
      }
    },
    onSuccess: (_, step) => {
      refresh();
      setReturning(false);
      setComment("");
      setArchiving(false);
      const theirs = one.data?.author_id !== me.data?.id;
      toast.success(step === "return" && theirs ? "Кейс вернулся автору — комментарий в обсуждении" : DONE[step]);
    },
    onError: (e: Error) => {
      toast.error(e.message);
      // A version conflict or a wrong status: someone changed the case meanwhile — show it as it is now.
      if (e instanceof ApiError && e.status === 409) refresh();
    },
  });

  const fromUrl = params.get("block");
  const block: Block = fromUrl && (BLOCKS as string[]).includes(fromUrl) ? (fromUrl as Block) : "case";
  const choose = (key: Block) => {
    const next = new URLSearchParams(params);
    next.set("block", key);
    setParams(next, { replace: true });
  };

  const bar = <AppBar fallback="/knowledge/cases" context="Кейсы" back={!embedded} />;
  if (one.isLoading) return <SkeletonList count={3} />;
  if (one.isError || !one.data) {
    const gone = one.error instanceof ApiError && one.error.status === 404;
    return (
      <div className="flex flex-col gap-3">
        {bar}
        <EmptyNote action={gone ? undefined : <Button size="compact" variant="secondary" onClick={() => void one.refetch()}>Повторить</Button>}>
          {gone ? "Кейс не найден." : "Не удалось загрузить кейс."}
        </EmptyNote>
      </div>
    );
  }

  const c = one.data;
  const mine = Boolean(me.data) && me.data?.id === c.author_id;
  // The server refuses a review without them; the button says so before it does.
  const ready = Boolean(c.problem_md?.trim() && c.solution_md?.trim());
  const busy = act.isPending;
  const pending = (step: Step) => act.isPending && act.variables === step;
  const tabs: BlockTab<Block>[] = [
    { key: "case", label: "Кейс" },
    { key: "uses", label: "Использования", count: c.uses.length },
    { key: "discussion", label: "Обсуждение" },
    { key: "files", label: "Файлы" },
  ];

  return (
    <div className="flex flex-col gap-3">
      {bar}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <CaseState status={c.status} />
        <Mark>{c.label || EXAMPLE_LABEL}</Mark>
        <span className="text-meta text-p-text-muted">{whoDid(nameOf(people.data, c.author_id), c.via_agent)} · {ago(c.created_at)}</span>
      </div>
      <div>
        <p className="text-[15px] font-semibold leading-5 text-p-text-muted">Кейс №{c.number}</p>
        <h1 className="break-words text-[21px] font-bold leading-[27px] text-p-text lg:text-[26px] lg:leading-8">{c.title}</h1>
      </div>
      {c.status === "accepted" && <Acceptance c={c} people={people.data} />}

      {c.status === "draft" && (
        <>
          <div className="flex flex-wrap gap-2">
            <Button variant="strong" className="flex-1" disabled={!ready || busy} loading={pending("submit")} onClick={() => act.mutate("submit")}>
              На проверку
            </Button>
            <Button variant="secondary" className="flex-1" disabled={busy} onClick={() => setEditing(true)}>Изменить</Button>
          </div>
          {!ready && <p className="text-meta text-p-text-muted">На проверку — когда заполнены задача клиента и решение.</p>}
        </>
      )}
      {c.status === "review" && (returning ? (
        <div className="space-y-2">
          <TextArea
            label={mine ? "Что доработать — необязательно" : "Что поправить — автор увидит это в обсуждении кейса"}
            hint="Коротко, до 580 знаков; подробности — в обсуждении"
            value={comment}
            rows={3}
            maxLength={580}
            autoFocus
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button variant="strong" className="flex-1" disabled={!mine && !comment.trim()} loading={pending("return")} onClick={() => act.mutate("return")}>
              {mine ? "Забрать на доработку" : "Вернуть"}
            </Button>
            <Button variant="secondary" className="flex-1" disabled={busy} onClick={() => setReturning(false)}>Отмена</Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button variant="strong" className="flex-1" disabled={busy} loading={pending("accept")} onClick={() => act.mutate("accept")}>Принять</Button>
          <Button variant="secondary" className="flex-1" disabled={busy} onClick={() => setReturning(true)}>
            {mine ? "Забрать на доработку" : "Вернуть"}
          </Button>
        </div>
      ))}
      {c.status === "accepted" && (
        <div className="flex flex-wrap gap-2">
          <Button variant="strong" className="flex-1" onClick={() => setUsing(true)}>Записать использование</Button>
          <Button variant="secondary" className="flex-1" onClick={() => setEditing(true)}>Изменить</Button>
        </div>
      )}
      {c.status === "archived" && (
        <div>
          <Button variant="strong" loading={pending("restore")} onClick={() => act.mutate("restore")}>Вернуть из архива</Button>
        </div>
      )}
      {c.status !== "archived" && (
        <div className="-mt-1 flex flex-wrap items-center gap-x-4">
          {c.status === "review" && !returning && (
            <button type="button" className="min-h-11 text-meta font-semibold text-p-link" onClick={() => setEditing(true)}>Изменить текст</button>
          )}
          <button type="button" className="min-h-11 text-meta font-semibold text-p-text-muted" disabled={busy}
            onClick={() => (archiving ? act.mutate("archive") : setArchiving(true))}>
            {archiving ? "Точно в архив" : "В архив"}
          </button>
          {archiving && (
            <>
              <button type="button" className="min-h-11 text-meta font-semibold text-p-link" onClick={() => setArchiving(false)}>Отмена</button>
              <span className="text-meta text-p-text-muted">Кейс уйдёт из списков; вернуть можно из архива.</span>
            </>
          )}
        </div>
      )}

      <BlockTabs label="Блоки кейса" id="case-blocks" tabs={tabs} value={block} onChange={choose} />
      <div {...blockPanel("case-blocks", block)} className="flex flex-col gap-3 pt-1">
        {block === "case" && <CaseText c={c} />}
        {block === "uses" && <Uses c={c} people={people.data} onRecord={() => setUsing(true)} />}
        {block === "discussion" && (
          <ChatThread key={c.id} context={{ parent_type: "case", parent_id: c.id }} hideSearch stickyComposer placeholder="Написать в кейс" />
        )}
        {block === "files" && <AttachmentsSection parentType="case" parentId={c.id} />}
      </div>

      {editing && <EditCaseSheet c={c} onClose={() => setEditing(false)} />}
      {using && <UseSheet c={c} onClose={() => setUsing(false)} />}
    </div>
  );
}

/** «Принял Костя · вчера, 15:05», and the person's own words when their agent passed them on. */
function Acceptance({ c, people }: { c: CaseDetailOut; people: UserOut[] | undefined }) {
  const who = nameOf(people, c.accepted_by) ?? "участник";
  return (
    <div className="space-y-1.5">
      <p className="text-meta text-p-text-muted">
        Принял {who}{c.accepted_via_agent ? " через агента" : ""}{c.accepted_at ? ` · ${ago(c.accepted_at)}` : ""}
      </p>
      {c.acceptance_md && (
        <blockquote className="break-words border-l-[3px] border-p-border-strong pl-3 text-body text-p-text">«{c.acceptance_md}»</blockquote>
      )}
    </div>
  );
}

/** The fields as the card reads them; what is still empty is named once, quietly. */
function CaseText({ c }: { c: CaseDetailOut }) {
  const demo = demoLink(c.demo_url);
  const fields = [{ key: "audience", label: "Для кого", text: c.audience }, ...CASE_TEXTS.map((f) => ({ key: f.key, label: f.label, text: c[f.key] }))];
  const empty = fields.filter((f) => !f.text?.trim()).map((f) => f.label);
  return (
    <>
      {demo && (
        <a href={demo} target="_blank" rel="noopener noreferrer"
          className="press-surface flex min-h-12 items-center gap-3 rounded-card border border-p-border bg-p-surface px-4 py-3">
          <ExternalLink className="h-5 w-5 shrink-0 text-p-link" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-semibold leading-5 text-p-link">Открыть демо</span>
            <span className="block truncate text-meta text-p-text-muted">{demo}</span>
          </span>
        </a>
      )}
      {fields.filter((f) => f.text?.trim()).map((f) => (
        <section key={f.key} className="space-y-1.5 rounded-card border border-p-border bg-p-surface px-4 py-3.5">
          <Caption>{f.label}</Caption>
          <Markdown className="text-body text-p-text" text={f.text} />
        </section>
      ))}
      {empty.length > 0 && <p className="text-meta text-p-text-muted">Не заполнено: {empty.join(", ")}.</p>}
      <section className="space-y-2">
        <Caption>Расход агентов</Caption>
        <div className="rounded-card border border-p-border bg-p-surface px-4 py-3">
          <p className="text-body font-semibold text-p-text">{spend(c.spend)}</p>
          <p className="text-micro text-p-text-muted">
            {c.spend
              ? `самоотчёт агентов: ${c.spend.records} ${pluralRu(c.spend.records, "запись", "записи", "записей")}${c.spend.records_without_tokens ? `, в ${c.spend.records_without_tokens} без токенов` : ""}`
              : "агенты ещё не записывали расход по этому кейсу"}
          </p>
        </div>
      </section>
    </>
  );
}

/** Where the case went out: the place, when, which version, who — and the post itself. */
function Uses({ c, people, onRecord }: { c: CaseDetailOut; people: UserOut[] | undefined; onRecord: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [removing, setRemoving] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: (useId: string) => Cases.uses.remove(c.id, useId),
    onSuccess: () => {
      setRemoving(null);
      for (const key of [["cases"], ["overview"]]) void qc.invalidateQueries({ queryKey: key });
      toast.success("Запись об использовании удалена");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!c.uses.length) {
    return c.status === "accepted" ? (
      <EmptyNote action={<Button size="compact" variant="secondary" onClick={onRecord}>Записать использование</Button>}>
        Кейс ещё нигде не выходил. Запиши, когда он появится в канале, группе, на сайте или в КП, — второй раз туда он не попадёт.
      </EmptyNote>
    ) : (
      <EmptyNote>Использовать можно принятый кейс. Здесь будет видно, где он выходил и в какой версии.</EmptyNote>
    );
  }
  return (
    <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
      {c.uses.map((u) => {
        const post = webUrl(u.url);
        const version = u.case_version === c.version ? `версия ${u.case_version}` : `версия ${u.case_version}, сейчас ${c.version}`;
        return (
          <li key={u.id} className="flex flex-col gap-1 border-b border-p-border px-4 py-3 last:border-0">
            <span className="break-words text-[15px] font-semibold leading-5 text-p-text">{u.place.name}</span>
            <span className="text-meta text-p-text-muted">
              {[PLACE_KIND[u.place.kind], formatDayMonth(new Date(u.used_at)), version, whoDid(nameOf(people, u.used_by), u.via_agent)].join(" · ")}
            </span>
            {u.note && <span className="break-words text-body text-p-text">{u.note}</span>}
            <span className="flex flex-wrap items-center gap-x-4">
              {post && (
                <a href={post} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-1 text-meta font-semibold text-p-link">
                  Публикация <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              )}
              {!post && u.url && <span className="break-all text-meta text-p-text-muted">{u.url}</span>}
              <button type="button" className="min-h-11 text-meta font-semibold text-p-text-muted" disabled={remove.isPending}
                onClick={() => (removing === u.id ? remove.mutate(u.id) : setRemoving(u.id))}>
                {removing === u.id ? "Точно удалить запись" : "Удалить запись"}
              </button>
              {removing === u.id && (
                <button type="button" className="min-h-11 text-meta font-semibold text-p-link" onClick={() => setRemoving(null)}>Отмена</button>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * An edit against the version it began from; a 409 opens the conflict sheet.
 * «Перечитать» starts again from the server's text, so the sheet closes.
 */
function EditCaseSheet({ c, onClose }: { c: CaseDetailOut; onClose: () => void }) {
  const toast = useToast();
  const [draft, setDraft] = useState<CaseDraft>(() => draftOf(c));
  const update = useVersionedUpdate<CasePatch, CaseDetailOut>({
    version: c.version,
    mutate: (body) => Cases.update(c.id, body),
    invalidate: [["cases"], ["overview"]],
    onSuccess: (saved) => {
      toast.success(c.status === "accepted" && saved.status === "review" ? "Сохранено — кейс снова на проверке" : "Кейс сохранён");
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });
  const patch = patchOf(draft, c);
  return (
    <>
      <Sheet
        open
        onClose={onClose}
        title={`Кейс №${c.number}`}
        footer={
          <Button block variant="strong" loading={update.pending} loadingText="Сохраняем…"
            disabled={!draft.title.trim() || Boolean(demoUrlError(draft.demo_url)) || Object.keys(patch).length === 0}
            onClick={() => void update.run(patch)}>
            Сохранить
          </Button>
        }
      >
        {c.status === "accepted" && (
          <p className="mb-3 rounded-xl border border-p-border-strong bg-p-surface-2 px-3 py-2.5 text-body text-p-text">
            Кейс принят. После правки он вернётся на проверку: использовать его снова можно будет, когда его примут.
          </p>
        )}
        <CaseFields draft={draft} onChange={setDraft} />
      </Sheet>
      <ConflictSheet
        conflict={update.conflict}
        attempted={update.attempted}
        onReload={() => {
          update.reload();
          onClose();
        }}
        onCancel={update.dismiss}
      />
    </>
  );
}

const KIND_OPTIONS = (Object.keys(PLACE_KIND) as CasePlaceKind[]).map((kind) => ({ value: kind, label: PLACE_KIND[kind] }));
const NEW_PLACE = { name: "", kind: "telegram" as CasePlaceKind, url: "" };

/** «12 сентября · версия 2» — the earlier use a refusal points at. */
function earlierLine(use: CaseUseOut): string {
  return [`Раньше: ${formatDayMonth(new Date(use.used_at))}`, `версия ${use.case_version}`, use.url].filter(Boolean).join(" · ");
}

/**
 * «Записать использование»: a place from the one list (D3) — or a new one; the
 * server knows «VK Просто» and «вк просто» are one place and offers the one
 * there is — the post's address and a note. Only an accepted case, once a place.
 */
function UseSheet({ c, onClose }: { c: CaseDetailOut; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const places = useQuery({ queryKey: ["cases", "places"], queryFn: () => Cases.places.list() });
  const [placeId, setPlaceId] = useState("");
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);
  const [place, setPlace] = useState(NEW_PLACE);
  // A place the server named (just added, or the one that already was) before the list re-reads.
  const [named, setNamed] = useState<CasePlaceOut | null>(null);
  const [placeNote, setPlaceNote] = useState<string>();
  const [error, setError] = useState<string>();

  const used = new Set(c.uses.map((u) => u.place.id));
  const known = [...(places.data ?? [])];
  if (named && !known.some((p) => p.id === named.id)) known.push(named);
  const options = known.filter((p) => !used.has(p.id)).map((p) => ({ value: p.id, label: `${p.name} · ${PLACE_KIND[p.kind]}` }));

  const addPlace = useMutation({
    mutationFn: () => Cases.places.create({ name: place.name.trim(), kind: place.kind, url: place.url.trim() || null }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ["cases", "places"] });
      setNamed(created);
      setPlaceId(created.id);
      setAdding(false);
      setPlace(NEW_PLACE);
      setPlaceNote(`Площадка «${created.name}» добавлена и выбрана.`);
    },
    onError: (e: Error) => {
      const existing = e instanceof ApiError && e.info?.error === "place_exists" ? (e.info.place as CasePlaceOut | undefined) : undefined;
      if (!existing) {
        setPlaceNote(e.message);
        return;
      }
      // One list keeps «the same place» spelled one way (D3): take the one there is.
      setNamed(existing);
      setAdding(false);
      setPlace(NEW_PLACE);
      if (used.has(existing.id)) {
        setPlaceId("");
        setPlaceNote(`Такая площадка уже есть — «${existing.name}», и этот кейс там уже выходил.`);
      } else {
        setPlaceId(existing.id);
        setPlaceNote(`Такая площадка уже есть — выбрана «${existing.name}».`);
      }
    },
  });

  const save = useMutation({
    mutationFn: () => Cases.uses.create(c.id, { place_id: placeId, url: url.trim() || null, note: note.trim() || null }),
    onSuccess: () => {
      for (const key of [["cases"], ["overview"]]) void qc.invalidateQueries({ queryKey: key });
      toast.success("Использование записано");
      onClose();
    },
    onError: (e: Error) => {
      const info = e instanceof ApiError ? e.info : undefined;
      const earlier = info?.error === "case_already_used_here" ? (info.use as CaseUseOut | undefined) : undefined;
      setError(earlier ? `${e.message}. ${earlierLine(earlier)}` : e.message);
      // Used there meanwhile: that place is taken, choose another one.
      if (earlier) setPlaceId("");
      // Not accepted any more, or used there meanwhile: the card shows it as it is.
      if (info?.error) void qc.invalidateQueries({ queryKey: ["cases", "one", c.id] });
    },
  });

  return (
    <Sheet
      open
      onClose={onClose}
      title={`Где вышел кейс №${c.number}`}
      footer={
        <div className="space-y-2">
          {error && <p role="alert" className="text-meta text-p-blocked">{error}</p>}
          <Button block variant="strong" loading={save.isPending} loadingText="Записываем…" disabled={!placeId || adding}
            onClick={() => {
              setError(undefined);
              save.mutate();
            }}>
            Сохранить
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-meta text-p-text-muted">Уйдёт версия {c.version}. На одну площадку кейс выходит один раз.</p>
        {places.isLoading ? <SkeletonList count={1} /> : (
          <SelectField
            label="Площадка"
            value={placeId}
            onChange={(value) => {
              setPlaceId(value);
              setPlaceNote(undefined);
              setError(undefined);
            }}
            options={options}
            placeholder={options.length ? "Выбери площадку" : "Площадок нет — добавь новую"}
          />
        )}
        {placeNote && <p className="text-meta text-p-text">{placeNote}</p>}
        {used.size > 0 && <p className="text-meta text-p-text-muted">Уже выходил: {c.uses.map((u) => u.place.name).join(", ")}.</p>}
        {adding ? (
          <div className="space-y-3 rounded-card border border-p-border px-3 py-3">
            <TextField label="Название площадки" hint="Как её все называют. Для КП площадка — клиент: «КП: ООО …»"
              value={place.name} maxLength={120} onChange={(e) => setPlace({ ...place, name: e.target.value })} />
            <SelectField label="Что это" value={place.kind} options={KIND_OPTIONS} placeholder="Выбери"
              onChange={(value) => value && setPlace({ ...place, kind: value as CasePlaceKind })} />
            <TextField label="Ссылка — необязательно" type="url" inputMode="url" autoCapitalize="off" placeholder="https://"
              value={place.url} maxLength={500} onChange={(e) => setPlace({ ...place, url: e.target.value })} />
            <div className="flex flex-wrap gap-2">
              <Button size="compact" variant="secondary" disabled={!place.name.trim()} loading={addPlace.isPending}
                onClick={() => {
                  setPlaceNote(undefined);
                  addPlace.mutate();
                }}>
                Добавить площадку
              </Button>
              <Button size="compact" variant="ghost" onClick={() => setAdding(false)}>Отмена</Button>
            </div>
          </div>
        ) : (
          <div>
            <Button size="compact" variant="ghost" icon={<Plus className="h-4 w-4" aria-hidden="true" />}
              onClick={() => {
                setAdding(true);
                setPlaceNote(undefined);
              }}>
              Новая площадка
            </Button>
          </div>
        )}
        <TextField label="Ссылка на публикацию — необязательно" type="url" inputMode="url" autoCapitalize="off" placeholder="https://"
          value={url} maxLength={500} onChange={(e) => setUrl(e.target.value)} />
        <TextField label="Заметка — необязательно" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Sheet>
  );
}
